import { ForwardedRef, forwardRef, memo, ReactElement, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Flex, Space, Switch, Tooltip, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { CopyOutlined, EditOutlined, EyeOutlined, HistoryOutlined } from '@ant-design/icons';
import { Guard, useGuard } from '@ohpen/react-authz';
import { Table, TableComponent, TableProps, CrudMode, TableColumns, getAllTableColumns, BoxedSwitch } from '@ohpen/enso-ui';

import { ActionEntityType } from '@pmp/api';
import { useCrudClone, useEntityExternalFilters, useListTableState, useTableView } from '@pmp/hooks';
import { cloneTableFields, resolveRowsByKey, type CloneRow, type CloneType, type ResolvedCloneRows } from '@pmp/assistant';
import { CrudTable, ProductCrudData } from '@pmp/components';
import { ChangeRequestsStateCrudTable } from '@pmp/pages';
import { ProductData } from '@pmp/types';
import { ChangeLogDrawer, ChangeLogTarget } from '../../changeLog';
import { useExportTemplatesStore } from '@pmp/stores';
import { formatDate, withSqeFilterModes } from '@pmp/utils';

import { productsColumns, productSqeFields } from '../tableConfig';
import { ProductsListProps, ProductsListComponent, ViewState, ProductsListFilters } from '../interfaces';
import { productsToProductsCrud, productsFiltersUrlCodec, hasLockedProducts } from '../utils';
import { removeReplacesCode } from '../cloneRows';
import { fetchProductsByCode, useProductsSqe } from '../useProductsSqe';
import { ProductCrudForm } from './ProductCrudForm';
import { ProductView } from './ProductView';

const { Text } = Typography;

const entityType = ActionEntityType.PRODUCTS;
const rowKey = 'code';
const CREATE_ONE = 'create-one';
const CREATE_IN_BULK = 'create-in-bulk';

// What the assistant may carry into a clone, read off the same column config the clone table is built from.
const cloneFields = cloneTableFields(productsColumns);

const getDefaultFilters = (): ProductsListFilters => ({
  activeToday: true,
});

const ProductsListBase = (
  { valueLists, additionalFields, fieldsLoading }: ProductsListProps,
  externalRef: ForwardedRef<ProductsListComponent>,
): ReactElement => {
  const tableRef = useRef<TableComponent | null>(null);
  const [viewData, setViewData] = useState<ViewState>({
    isOpen: false,
    record: null,
  });
  const [crudData, setCrudData] = useState<ChangeRequestsStateCrudTable>({
    isOpen: false,
    isLoading: false,
    data: null,
    mode: null,
    entityType,
  });
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();
  const { tableViews, saveTableView, deleteTableView } = useTableView(ActionEntityType.PRODUCTS);
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const defaultFilters = useMemo(getDefaultFilters, []);
  const [{ activeToday }, setFilters] = useEntityExternalFilters<ProductsListFilters>(
    entityType,
    defaultFilters,
    productsFiltersUrlCodec,
  );
  const [selectedRows, setSelectedRows] = useState<ProductData[]>([]);
  const [changeLogTarget, setChangeLogTarget] = useState<ChangeLogTarget | null>(null);
  const { allowed: showRowSelection } = useGuard({
    permissions: ['products:update'],
    flags: ['enableProductsCrud'],
  });
  const { t } = useTranslation();

  // The products search has no fields for the additional fields, so their columns offer no filter or sort.
  const columns: TableColumns<ProductData> = useMemo(() => {
    const allColumns = getAllTableColumns(productsColumns, additionalFields).map(column =>
      column.options?.isCustom ? { ...column, options: { ...column.options, filter: undefined, sorter: false } } : column,
    );
    return withSqeFilterModes(allColumns, productSqeFields);
  }, [additionalFields]);

  const {
    isFetching,
    data: dataSource,
    total,
    setServerSideFiltering,
    exportFetchAll,
    refresh: refreshTable,
  } = useProductsSqe(columns, valueLists, activeToday);

  useImperativeHandle(externalRef, () => {
    return { refreshTable };
  });

  const tableState = useListTableState<ProductData>(entityType, rowKey, tableRef, columns);

  const resetSelectedRows = useCallback(() => {
    tableRef.current?.resetSelectedRows();
  }, []);

  const onView = useCallback((record: ProductData) => {
    setActiveRowId(record[rowKey] ?? null);
    setViewData({ isOpen: true, record });
  }, []);

  const onCreate = useCallback((key?: string) => {
    if (key === CREATE_IN_BULK) {
      setCrudData({ isOpen: true, data: null, mode: CrudMode.CREATE, entityType });
      return;
    }
    setIsCreateFormOpen(true);
  }, []);

  const onCloseCreateForm = useCallback(() => {
    setIsCreateFormOpen(false);
  }, []);

  const onEdit = useCallback((record: ProductData) => {
    const data: ProductCrudData[] = productsToProductsCrud([record]);

    setActiveRowId(record[rowKey] ?? null);
    setCrudData({ isOpen: true, data, mode: CrudMode.EDIT, entityType });
  }, []);

  const onClone = useCallback((record: ProductData) => {
    const data: ProductCrudData[] = removeReplacesCode(productsToProductsCrud([record]));

    setActiveRowId(record[rowKey] ?? null);
    setCrudData({ isOpen: true, data, mode: CrudMode.CLONE, entityType, hasLockedOriginals: hasLockedProducts([record]) });
  }, []);

  const onBulkEdit = useCallback((selectedRows: ProductData[]) => {
    const data: ProductCrudData[] = productsToProductsCrud(selectedRows);

    setCrudData({ isOpen: true, data, mode: CrudMode.BULK_EDIT, entityType });
  }, []);

  const onBulkClone = useCallback((selectedRows: ProductData[]) => {
    const data: ProductCrudData[] = removeReplacesCode(productsToProductsCrud(selectedRows));

    setCrudData({ isOpen: true, data, mode: CrudMode.BULK_CLONE, entityType, hasLockedOriginals: hasLockedProducts(selectedRows) });
  }, []);

  // The clone the assistant proposes, opened exactly as the Clone buttons above open theirs. The rows it
  // hands over are the grid's own, with its changes already on them; the crud rows are built from the
  // originals so each copy keeps the code it came from, and the changes are laid over that. Passing the
  // originals as well is what makes the proposed cells read as changed in the table the user then reads.
  // The merged rows go through removeReplacesCode last: they carry the grid row's own fields, and a copy
  // that keeps the source's lock reads as caught in the source's pending change request.
  const onCloneProposed = useCallback((rows: CloneRow[], originals: CloneRow[], cloneType: CloneType) => {
    const originalData: ProductCrudData[] = removeReplacesCode(productsToProductsCrud(originals as ProductData[]));
    const data: ProductCrudData[] = removeReplacesCode(originalData.map((row, index) => ({ ...row, ...rows[index] }) as ProductCrudData));

    setActiveRowId(rows.length === 1 ? ((rows[0][rowKey] as string) ?? null) : null);
    setCrudData({
      isOpen: true,
      data,
      originalData,
      cloneType,
      mode: rows.length === 1 ? CrudMode.CLONE : CrudMode.BULK_CLONE,
      entityType,
      hasLockedOriginals: hasLockedProducts(originals as ProductData[]),
    });
  }, []);

  const onCloseCrud = useCallback(() => {
    setCrudData({ isOpen: false, data: null, mode: null, entityType });
  }, []);

  const onCloseView = useCallback(() => {
    setViewData({ isOpen: false, record: null });
  }, []);

  const onChangeActiveToday = useCallback(
    (checked: boolean) => {
      setFilters(prev => ({ ...prev, activeToday: checked }));
      tableRef.current?.resetPage();
    },
    [setFilters],
  );

  // Bulk edit/clone need the rows themselves; the assistant only ever gets their ids, and gets
  // nothing at all when the assistant flag is off (the hook returns no callback then).
  const onChangeSelectedRows = useCallback(
    (selectedRows: ProductData[]) => {
      setSelectedRows(selectedRows);
      tableState.onChangeSelectedRows?.(selectedRows);
    },
    [tableState],
  );

  const getTemplates = useCallback(() => getExportTemplates(ActionEntityType.PRODUCTS), [getExportTemplates]);

  const createItems: MenuProps['items'] = useMemo(
    () => [
      { key: CREATE_ONE, label: t('pmp.products.labels.createProduct') },
      { key: CREATE_IN_BULK, label: t('pmp.products.labels.createProductsInBulk') },
    ],
    [t],
  );

  const getRowActions: (record: ProductData) => ReactElement[] = useCallback(
    record => {
      const editTitle = record.lockedByActionId ? (
        <>
          {t('global.buttons.editLockedByChangeRequest')}
          <Link to={`/change-requests?search-change-requests=${encodeURIComponent(record.lockedByActionId)}`}>
            {record.lockedByActionId}
          </Link>
        </>
      ) : (
        t('enso.buttons.edit')
      );
      const rowActions = [
        <Tooltip key="view" title={t('enso.buttons.view')} placement="top">
          <Button type="text" aria-label="View Product" icon={<EyeOutlined />} onClick={() => onView(record)} />
        </Tooltip>,
        <Guard key="edit" permissions={['products:update']} flags={['enableProductsCrud']}>
          <Tooltip key="edit" title={editTitle} placement="top">
            <Button
              type="text"
              aria-label="Edit Product"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
              disabled={!!record.lockedByActionId}
            />
          </Tooltip>
          <Tooltip key="clone" title={t('global.buttons.clone')} placement="top">
            <Button type="text" aria-label="Clone Product" icon={<CopyOutlined />} onClick={() => onClone(record)} />
          </Tooltip>
        </Guard>,
        <Guard key="changeLog" flags={['enableChangeLog']}>
          <Tooltip title={t('lender.changeLog.labels.viewChangeLog')} placement="top">
            <Button
              type="text"
              aria-label="View change log"
              icon={<HistoryOutlined />}
              onClick={() =>
                setChangeLogTarget({ entityType: ActionEntityType.PRODUCTS, id: record.code ?? '', name: record.name ?? record.code ?? '' })
              }
            />
          </Tooltip>
        </Guard>,
      ];
      return rowActions;
    },
    [onClone, onEdit, onView, t],
  );

  const disabledBulkEdit: boolean = useMemo(() => selectedRows?.some(record => !!record.lockedByActionId) ?? false, [selectedRows]);

  const isLoading = fieldsLoading || isFetching;

  // The rows a proposal names: the ones on the page in view, and the rest searched for with the same Active
  // today, so a product the list holds can be cloned from any page and one it does not hold cannot be.
  // Matched without case: a product code is unique without it, and a code reaches here in whatever case it was
  // written in rather than the one the row carries. Naming none means the ones the user has selected, which is
  // the gesture the proposal follows.
  const resolveCloneRows = useCallback(
    async (keys: readonly string[], signal?: AbortSignal): Promise<ResolvedCloneRows> => {
      if (keys.length === 0) return { rows: selectedRows as CloneRow[], missing: [] };

      return resolveRowsByKey(
        keys,
        rowKey,
        dataSource as CloneRow[],
        async (codes, lookupSignal) => (await fetchProductsByCode(codes, activeToday, lookupSignal)) as CloneRow[],
        signal,
      );
    },
    [dataSource, selectedRows, activeToday],
  );

  // Registered only for a user the portal would let clone a product anyway: the same permission and flag the
  // Clone buttons are guarded by.
  useCrudClone(showRowSelection ? entityType : undefined, {
    fields: cloneFields,
    resolveRows: resolveCloneRows,
    openWith: onCloneProposed,
  });

  const tableProps: TableProps<ActionEntityType, ProductData> = useMemo(
    () => ({
      ...tableState,
      id: entityType,
      itemsLabel: t('pmp.products.labels.products'),
      ref: tableRef,
      dataSource,
      columns,
      rowKey,
      pagination: { total, defaultPageSize: 20 },
      serverSideFiltering: true,
      setServerSideFiltering,
      scroll: { x: 'max-content', y: '100%' },
      resizableColumns: true,
      valueLists,
      loading: isLoading,
      activeRowId,
      initialGlobalSearchColumn: rowKey,
      getRowActions,
      showRowSelection,
      showViews: true,
      tableViews,
      saveTableView,
      deleteTableView,
      onChangeSelectedRows,
      additionalFields: true,
    }),
    [
      tableState,
      t,
      dataSource,
      columns,
      valueLists,
      isLoading,
      activeRowId,
      getRowActions,
      showRowSelection,
      tableViews,
      saveTableView,
      deleteTableView,
      onChangeSelectedRows,
      total,
      setServerSideFiltering,
    ],
  );

  const exportFileName = `Products on ${formatDate()}`;

  return (
    <>
      <Table {...tableProps}>
        <>
          <Table.ActionsBar>
            <Flex justify="space-between" gap="middle">
              <Space size="middle">
                <Table.GlobalSearch />
                <BoxedSwitch>
                  <Text>{t('pmp.products.labels.activeToday')}</Text>
                  <Switch value={activeToday} onChange={onChangeActiveToday} />
                </BoxedSwitch>
              </Space>
              <Space size="middle">
                <Table.ResetFiltersButton />
                <Table.SelectColumnsButton />
                <Guard flags={['enableExports']}>
                  <Table.ExportButton
                    fileName={exportFileName}
                    sheetName="Products"
                    disabled={isLoading}
                    loadingTemplates={isLoadingExportTemplates}
                    allowSearch={true}
                    getTemplates={getTemplates}
                    modalDefaults={{ columnScope: 'template' }}
                    editableFileName
                    exportFetchAll={exportFetchAll}
                  />
                </Guard>
                <Guard permissions={['products:create']} flags={['enableProductsCrud']}>
                  <Table.CreateButton items={createItems} onClick={onCreate} />
                </Guard>
              </Space>
            </Flex>
          </Table.ActionsBar>
          <Table.BulkBar xItemsSelectedLabel="pmp.products.labels.xProductsSelected">
            <Space size="middle">
              <Table.BulkCloneButton onClick={onBulkClone} />
              <Table.BulkEditButton
                onClick={onBulkEdit}
                disabled={disabledBulkEdit}
                tooltip={disabledBulkEdit ? t('global.labels.bulkEditLockedByChangeRequest') : undefined}
              />
              <Guard flags={['enableExports']}>
                <Table.ExportButton
                  fileName={exportFileName}
                  sheetName="Products"
                  disabled={isLoading}
                  loadingTemplates={isLoadingExportTemplates}
                  allowSearch={true}
                  getTemplates={getTemplates}
                  modalDefaults={{ columnScope: 'template' }}
                  editableFileName
                  exportView
                />
              </Guard>
            </Space>
          </Table.BulkBar>
        </>
      </Table>
      {viewData.isOpen && (
        <ProductView record={viewData.record} valueLists={valueLists} additionalFields={additionalFields} onClose={onCloseView} />
      )}
      {isCreateFormOpen && (
        <ProductCrudForm
          valueLists={valueLists}
          additionalFields={additionalFields}
          refreshTable={refreshTable}
          onClose={onCloseCreateForm}
        />
      )}
      <ChangeLogDrawer target={changeLogTarget} onClose={() => setChangeLogTarget(null)} />
      {crudData.isOpen && (
        <CrudTable
          entityType={ActionEntityType.PRODUCTS}
          data={crudData.data}
          originalData={crudData.originalData}
          columns={productsColumns}
          mode={crudData.mode}
          cloneType={crudData.cloneType}
          additionalFields={additionalFields}
          valueLists={valueLists}
          hasLockedOriginals={crudData.hasLockedOriginals}
          setActiveRowId={setActiveRowId}
          refreshTable={refreshTable}
          resetSelectedRows={resetSelectedRows}
          onClose={onCloseCrud}
        />
      )}
    </>
  );
};

export const ProductsList = memo(forwardRef(ProductsListBase));
